import { Checkbox, FormControlLabel, Grid, useEventCallback } from '@mui/material';
import { memo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { FormikProps } from 'formik';
import { FilterDomesticPartnershipVM } from 'practice/view-models/practice-edit-residence';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { noop } from 'shell/utils/noop';
import { DatePicker } from '@mui/lab';
import { ModalDomesticVM } from '..';
import { MilanMuncipality } from 'practice/constants/muncipality';
import FilterCohabitationStreet from 'shell/components/shared/search-cohabitation/filter-cohabitation';
import { FiltersFormCohabitation } from 'cohabitation/view-models/filter-cohabitation';

interface FormProps {
  formContext: FormikProps<ModalDomesticVM>;
}

const FormComponent = ({ formContext }: FormProps): React.ReactElement => {
  const onChange = useEventCallback(
    (
      key: keyof FilterDomesticPartnershipVM | keyof FiltersFormCohabitation,
      value:
        | FilterDomesticPartnershipVM[keyof FilterDomesticPartnershipVM]
        | FiltersFormCohabitation[keyof FiltersFormCohabitation]
    ) => {
      formContext.setFieldValue('filterDomesticPartnership', {
        ...(formContext.values.filterDomesticPartnership || {}),
        [key]: value,
      });
    }
  );

  const onChangeMunicipalityList = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    onChange('municipality', municipality);
  });

  const onChangeValues = useEventCallback((values: FiltersFormCohabitation) => {
    formContext.setValues({
      ...formContext.values,
      filterDomesticPartnership: {
        ...(formContext.values.filterDomesticPartnership || {}),
        IdCardMunicipality: values.codeMunicipality,
        ...values,
      },
    });
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              id="isNationalResearch"
              checked={formContext.values.filterDomesticPartnership?.isNationalResearch || false}
              onChange={() =>
                onChange(
                  'isNationalResearch',
                  !formContext.values.filterDomesticPartnership?.isNationalResearch
                )
              }
              inputProps={{ 'aria-label': 'Ricerca nazionale' }}
              disabled
            />
          }
          label="Ricerca nazionale"
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <MunicipalityList
          label="Comune"
          value={MilanMuncipality}
          onChange={onChangeMunicipalityList}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Provincia"
          aria-label="Provincia"
          id="province"
          name="province"
          fullWidth
          type="text"
          variant="outlined"
          value="MI"
          onChange={noop}
          disabled
        />
      </Grid>
      <FilterCohabitationStreet
        values={formContext.values.filterDomesticPartnership as FiltersFormCohabitation}
        onChange={
          onChange as (
            key: keyof FiltersFormCohabitation,
            value: FiltersFormCohabitation[keyof FiltersFormCohabitation]
          ) => void
        }
        onChangeValues={onChangeValues}
      />
      <Grid item xs={12} md={7}>
        <PmiTextField
          label="Identificativo scheda attribuito dal Comune"
          aria-label="Identificativo scheda attribuito dal Comune"
          id="IdCardMunicipality"
          name="IdCardMunicipality"
          fullWidth
          type="text"
          variant="outlined"
          value={formContext.values.filterDomesticPartnership?.IdCardMunicipality || ''}
          onChange={(event) => onChange('IdCardMunicipality', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <PmiTextField
          label="Codice ISTAT Comune"
          aria-label="Codice ISTAT Comune"
          id="istat"
          name="istat"
          fullWidth
          type="text"
          variant="outlined"
          value={formContext.values.filterDomesticPartnership?.istat || ''}
          onChange={(event) => onChange('istat', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={7}>
        <PmiTextField
          label="Identificativo scheda attribuito ANPR"
          aria-label="Identificativo scheda attribuito ANPR"
          id="IdCardAnpr"
          name="IdCardAnpr"
          fullWidth
          type="text"
          variant="outlined"
          value={formContext.values.filterDomesticPartnership?.IdCardAnpr || ''}
          onChange={(event) => onChange('IdCardAnpr', event.target.value)}
        />
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Tipo scheda"
            aria-label="Tipo scheda"
            id="domestic"
            name="domestic"
            fullWidth
            type="text"
            variant="outlined"
            value="Convivenza" // fixed 4 = convivenza
            onChange={noop}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Data rif. scheda"
            aria-label="Data rif. scheda"
            value={formContext.values.filterDomesticPartnership?.date || null}
            onChange={(date) => {
              onChange('date', date);
            }}
            renderInput={(params) => <PmiTextField id="date" {...params} fullWidth helperText="" />}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

FormComponent.displayName = 'Accordion Filter - Form component';

const Form = memo(FormComponent);

export default Form;
